import { Component, inject } from '@angular/core';
import { OccupationsService } from '../services/occupations.service';
import { Occupation, OccupationPost } from '../services/occupation.interface';
import { AppConstants } from '../../../constants/AppConstants';
import { SnackbarService } from '@/app/shared/services/snackbar.service';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { noSoloEspacios } from '../../clientes/pages/clientes-form-component/validations';
import { TitlePagesComponent } from '../../../shared/components/title-pages-component/title-pages-component';
import { ConfirmDeleteDialog } from '../../../shared/components/confirm-delete-dialog/confirm-delete-dialog';
import { TableWithInputsComponent } from '../../../shared/components/table-with-inputs-component/table-with-inputs-component';
import { ModalPasosCampana } from '../../../shared/components/modal-pasos-campana/modal-pasos-campana';
import { Client } from '../../cliente/models/cliente.interface';
import { ClientStore } from '../../../core/states/clientes-state/client-state.store';
import { Departament } from '../../departamentos/services/departament.interface';
import { DepartamentService } from '../../departamentos/services/departament.service';
import { BulkUploadModalComponent } from '../../../shared/components/bulk-upload-modal/bulk-upload-modal.component';
import { BulkParseService } from '../../../shared/services/bulk-upload-modal/bulk-parse.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-ocupacion-component',
  standalone: true,
  imports: [
    TitlePagesComponent,
    TableWithInputsComponent,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    ConfirmDeleteDialog,
    ModalPasosCampana,
    BulkUploadModalComponent,
  ],
  templateUrl: './ocupacion-component.html',
  styleUrl: './ocupacion-component.scss',
})
export class OcupacionComponent {
  pageData = {
    title: AppConstants.OCCUPATION_TITLE,
    subTitle: AppConstants.OCCUPATION_SUBTITLE,
  };
  columnDefinitions = [
    { key: 'name', displayName: 'Ocupación' },
    { key: 'acciones', displayName: 'Acciones' },
  ];
  protected readonly AppConstants = AppConstants;
  protected occupations: Occupation[] = [];
  // para el formulario
  protected showForm: boolean = false;
  protected submitted: boolean = false;
  // para mostrar u ocultar el boton de nuevo
  protected showBtn: boolean = true;
  // para el modar de confirmación de eliminacion
  protected showModal: boolean = false;
  protected message!: string;
  protected occupationToDelete!: Occupation;
  protected showModalPasosCampaign!: boolean;
  protected showBulkUploadModal = false;
  private occupationService: OccupationsService = inject(OccupationsService);
  private departamentService: DepartamentService = inject(DepartamentService);
  private store = inject(ClientStore);
  private formBuilder: FormBuilder = inject(FormBuilder);
  //Formulario para registrar la ocupación;
  formOccupation = this.formBuilder.group({
    name: new FormControl('', [Validators.required, noSoloEspacios()]),
    description: new FormControl(''),
  });
  private snackbarService = inject(SnackbarService);
  private bulkParse = inject(BulkParseService);
  private route = inject(ActivatedRoute);
  // variables para el control del fujlo de creación de clientes.
  private client!: Client | null;
  private deps: Departament[] = [];

  private departmentId: string | null = null;
  private router = inject(Router);

  get name() {
    return this.formOccupation.get('name');
  }

  get description() {
    return this.formOccupation.get('description');
  }

  // ===== CARGA MASIVA =====
  async onBulkConfirm(file: File) {
    try {
      // Lee y valida la plantilla: título + encabezados "Nombre" / "Descripción"
      const { validNames, invalid, duplicates } =
        await this.bulkParse.parseCreateFile(file);

      // 1) Priorizar error de FORMATO / ENCABEZADOS (el servicio marca row:1 con mensaje pedagógico)
      const headerIssue = invalid.find((e) => e.row === 1);
      if (headerIssue) {
        this.snackbarService.showError(headerIssue.reason);
        return;
      }

      // 2) Si hay otras filas inválidas (datos), no enviamos
      if (invalid.length > 0) {
        this.snackbarService.showError(
          `Archivo con errores. Filas inválidas: ${invalid.length}. Duplicadas en el archivo: ${duplicates.length}.`
        );
        return;
      }

      // 3) Sin inválidas pero sin filas válidas
      if (validNames.length === 0) {
        this.snackbarService.showError('No se encontraron filas válidas.');
        return;
      }

      // 4) Construir payload [{ name }] (por ahora solo enviamos nombre)
      const payload = validNames.map((nombre) => ({ name: nombre }));

      // 5) Quitar los que ya existen en la tabla local para evitar 409
      const existing = new Set(
        this.occupations.map((o) => o.name.trim().toLowerCase())
      );
      const payloadFiltered = payload.filter(
        (p) => !existing.has(p.name.trim().toLowerCase())
      );
      const alreadyExisting = payload.length - payloadFiltered.length;

      if (payloadFiltered.length === 0) {
        this.snackbarService.showError(
          'Todos los registros del archivo ya existen. No se enviaron datos.'
        );
        return;
      }

      // 6) POST batch a ocupaciones

      if (!this.client) {
        return;
      }
      this.occupationService
        .createOccupationsBatch(
          this.client.publicId,
          this.departmentId!,
          payloadFiltered
        )
        .subscribe({
          next: (created) => {
            this.occupations = [...this.occupations, ...created];
            this.snackbarService.showSuccess(
              `Creadas: ${created.length}. Duplicadas internas: ${duplicates.length}` +
                (alreadyExisting > 0 ? `. Ya existían: ${alreadyExisting}` : '')
            );
            this.showBulkUploadModal = false;
            localStorage.removeItem('validateOccupation');
          },
          error: (err) => {
            if (err?.status === 409) {
              this.snackbarService.showError(
                'Algunos nombres ya existen y no se pudieron crear. Revisa tu archivo y la lista actual.'
              );
            } else {
              this.snackbarService.showError(
                `Error al crear ocupaciones en lote: ${
                  err?.message ?? 'desconocido'
                }`
              );
            }
          },
        });
    } catch (e: any) {
      this.snackbarService.showError(
        `No se pudo leer el archivo: ${e?.message ?? e}`
      );
    }
  }

  onBulkClose() {
    this.showBulkUploadModal = false;
  }

  // Opción 1 del menú → mostrar form
  handleAddSingle(): void {
    this.showForm = true;
    this.showBtn = false;
  }

  // Opción 2 del menú → Carga masiva
  handleBulkUpload(): void {
    this.showBulkUploadModal = true;
  }

  ngOnInit(): void {
    // Verificar si viene del flujo de campañas (query parameter showModal=true)
    this.departmentId =
      this.route.snapshot.queryParams['departmentId'].toString();
    this.showModalPasosCampaign = false;

    if (!this.departmentId) {
      this.snackbarService.showError('ID de departamento no proporcionado.');
      this.router.navigate(['/clientes']);
    }
    this.client = this.store.client();
    if (this.client) {
      //obtenemos las ocupaciones
      this.occupationService
        .getOccupations(this.client.publicId, this.departmentId!)
        .subscribe((data: Occupation[]) => {
          this.occupations = data;
          //validamos si hay ocupaciones para obtener los departamentos y activar los links
          if (this.occupations.length > 0) {
            this.store.setOccupations(this.occupations);
            if (this.client) {
              this.departamentService
                .getDepartaments(this.client.publicId)
                .subscribe((data: Departament[]) => {
                  this.deps = data;
                  this.store.setDepartaments(this.deps);
                });
            }
          }
        });
    }
  }

  closeModalPasosCampaign(): void {
    this.showModalPasosCampaign = false;
  }

  //cierra el formulario de registro.
  handleCancelAdd(): void {
    this.showForm = false;
    this.showBtn = true;
  }

  //realiza el guardado de la ocupación
  handleSubmit() {
    this.submitted = true;
    if (this.formOccupation.invalid) {
      return;
    }

    const occupation: OccupationPost = {
      name: this.formOccupation.value.name ?? '',
    };

    if (!this.client) {
      return;
    }

    this.occupationService
      .createOccupation(this.client.publicId, this.departmentId!, occupation)
      .subscribe({
        next: (createdOccupation: Occupation) => {
          this.occupations.push(createdOccupation);
          this.snackbarService.showSuccess('Ocupación creada con éxito');
          this.store.setOccupations(this.occupations);
          this.formOccupation.reset();
          this.showForm = false;
          this.showBtn = true;
          this.submitted = false;
        },
        error: (err) => {
          this.snackbarService.showError(
            'Error al crear la ocupación ' + err.message
          );
        },
      });
  }

  handleSaveEdit(item: any) {
    if (!this.client) {
      return;
    }
    this.occupationService
      .updateOccupation(item.id, item, this.client.publicId, this.departmentId!)
      .subscribe({
        next: (updatedItem) => {
          const index = this.occupations.findIndex((o) => o.id === item.id);
          if (index !== -1) {
            this.occupations[index] = { ...item };
          }
          this.snackbarService.showSuccess('Ocupación editada con éxito');
        },
        error: (err) => {
          this.snackbarService.showError(
            'Error al editar la ocupación ' + err.message
          );
        },
      });
  }

  //muestra el modal de confirmación de eliminación
  showConfirmDeleteDialog = (occupation: Occupation) => {
    //mensaje para la confirmación de eliminacion
    this.message = 'Se eliminará la ocupación: ' + occupation.name;
    this.occupationToDelete = occupation;
    this.showModal = true;
  };

  //cierra el modal de confirmación de eliminación
  handleCancelDeleteDialog() {
    this.showModal = false;
  }

  //elimina el registro de departamento
  handleDelete() {
    if (!this.client) {
      return;
    }
    this.occupationService
      .deleteOccupation(
        this.occupationToDelete.id,
        this.client.publicId,
        this.departmentId!
      )
      .subscribe({
        next: () => {
          this.showModal = false;
          this.occupations = this.occupations.filter(
            (o) => o.id !== this.occupationToDelete.id
          );
          this.store.setOccupations(this.occupations);
          this.snackbarService.showSuccess('Ocupacion eliminado con éxito');
        },
        error: (err) => {
          this.snackbarService.showError(
            'Error al eliminar la ocupacion: ' + err.message
          );
        },
      });
  }

  // Descarga de formato (si lo manejas desde el padre con (downloadFormat))
  onDescargarFormato(resource: string) {
    this.snackbarService.showInfo(`Descargando formato de ${resource}…`);
  }
}
